// src/components/kit/epk/EpkManagement.tsx

import { useNavigate } from 'react-router-dom';
import { useEpkPage, useDeleteEpkPage } from '@/hooks/useEpk';
import { useSubscriptionLimits } from '@/hooks/useSubscription';
import Spinner from '@/components/common/Spinner';
import EmptyState from '@/components/common/EmptyState';
import UpgradeModal from '@/components/common/UpgradeModal';
import { useState } from 'react';

export default function EpkManagement() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useEpkPage();
  const deleteMutation = useDeleteEpkPage();
  const { canAccessAnalytics } = useSubscriptionLimits();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // No page exists yet — show empty state
  const page = data?.page;
  if (!page || error) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          title="Create your Electronic Press Kit"
          description="Build a professional, hosted EPK for your band. Share your bio, photos, music, press coverage, and contact info — all in one place."
          icon={
            <svg className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          }
          action={
            <button
              onClick={() => navigate('/app/kit/epk/edit')}
              className="rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-primary-700"
            >
              Create EPK
            </button>
          }
        />
      </div>
    );
  }

  // Page exists — show management view
  const handleDelete = async () => {
    await deleteMutation.mutateAsync();
    setShowDeleteConfirm(false);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(page.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAnalyticsClick = () => {
    if (canAccessAnalytics) {
      navigate('/app/kit/epk/analytics');
    } else {
      setShowUpgradeModal(true);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-neutral-800">Electronic Press Kit</h1>
          <p className="mt-1 text-sm text-neutral-500">Manage your public EPK</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAnalyticsClick}
            className="rounded-lg border border-neutral-300 bg-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-300"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate('/app/kit/epk/edit')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-primary-700"
          >
            Edit EPK
          </button>
        </div>
      </div>

      {/* Page card */}
      <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-4 sm:p-6">
        <div className="flex items-start gap-4">
          {page.hero_image_url ? (
            <img src={page.hero_image_url} alt={page.artist_name} className="h-16 w-16 flex-shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-neutral-300">
              <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold text-neutral-800">{page.artist_name}</h2>
            {page.tagline && <p className="mt-0.5 truncate text-sm text-neutral-500">{page.tagline}</p>}
            <div className="mt-2 flex items-center gap-3">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${page.published ? 'bg-green-900/30 text-green-400' : 'bg-neutral-300 text-neutral-500'}`}>
                {page.published ? 'Published' : 'Draft'}
              </span>
              {page.genre && <span className="text-xs text-neutral-500">{page.genre}</span>}
            </div>
          </div>
        </div>

        {/* Public URL */}
        <div className="mt-4 rounded-lg border border-neutral-300 bg-neutral-100 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs text-neutral-500">Public URL</p>
              <p className="mt-0.5 break-all text-sm font-medium text-primary-600">{page.public_url}</p>
            </div>
            <button onClick={handleCopyUrl} className="flex-shrink-0 self-start rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-300 sm:self-auto">
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 p-3 text-center">
            <p className="text-xs text-neutral-500">Layout</p>
            <p className="mt-0.5 text-sm font-medium capitalize text-neutral-700">{page.layout}</p>
          </div>
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 p-3 text-center">
            <p className="text-xs text-neutral-500">Palette</p>
            <p className="mt-0.5 text-sm font-medium capitalize text-neutral-700">{page.palette_id}</p>
          </div>
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 p-3 text-center">
            <p className="text-xs text-neutral-500">Font Theme</p>
            <p className="mt-0.5 text-sm font-medium capitalize text-neutral-700">{page.font_theme_id}</p>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mt-8 rounded-xl border border-red-900/30 bg-red-900/10 p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-red-400">Danger Zone</h3>
        <p className="mt-1 text-sm text-neutral-500">Permanently delete your EPK and all associated data.</p>
        {showDeleteConfirm ? (
          <div className="mt-3 flex items-center gap-2">
            <button onClick={handleDelete} disabled={deleteMutation.isPending} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-500">
              {deleteMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
            </button>
            <button onClick={() => setShowDeleteConfirm(false)} className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setShowDeleteConfirm(true)} className="mt-3 rounded-lg border border-red-600/50 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-900/20">Delete EPK</button>
        )}
      </div>

      {/* Upgrade modal for analytics */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
}
